import genPicks from "./genPicks.ts";
import logLotteryChances from "./logLotteryChances.ts";
import logLotteryWinners from "./logLotteryWinners.ts";
import divideChancesOverTiedTeams from "./divideChancesOverTiedTeams.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, random } from "../../util/index.ts";
import type {
	Conditions,
	DraftLotteryResult,
	DraftType,
	DraftPickWithoutKey,
	DraftPick,
	GameAttributesLeague,
} from "../../../../common/types.ts";
import genOrderGetPicks from "./genOrderGetPicks.ts";
import getTeamsByRound from "./getTeamsByRound.ts";
import { COLA_ALPHA, PHASE } from "../../../../common/constants.ts";
import { league } from "../index.ts";
import getNumPlayoffTeams from "../season/getNumPlayoffTeams.ts";
import {
	getNumLotteryTeams,
	updateLotteryChancesAfterLottery,
} from "./cola.ts";

type ReturnVal = {
	draftLotteryResult:
		| (DraftLotteryResult & {
				draftType: Exclude<
					DraftType,
					"random" | "noLottery" | "noLotteryReverse" | "freeAgents"
				>;
		  })
		| undefined;
	draftPicks: DraftPick[];
};

const LOTTERY_DRAFT_TYPES = [
	"nba1994",
	"nba2019",
	"coinFlip",
	"randomLottery",
	"randomLotteryFirst3",
	"nba1990",
	"nhl2017",
	"nhl2021",
	"mlb2022",
	"custom",
	"cola",
] as const;

// chances does not have to be the perfect length. If chances is too long for numLotteryTeams, it will be truncated. If it's too short, the last entry will be repeated until it's long enough.
const getLotteryInfo = (draftType: DraftType, numLotteryTeams: number) => {
	if (draftType === "coinFlip") {
		return {
			numToPick: 2,
			chances: [1, 1, 0],
		};
	}

	if (draftType === "randomLottery") {
		return {
			numToPick: numLotteryTeams,
			chances: [1],
		};
	}

	if (draftType === "randomLotteryFirst3") {
		return {
			numToPick: 3,
			chances: [1],
		};
	}

	if (draftType === "nba1990") {
		const chances = [];
		for (let i = numLotteryTeams; i > 0; i--) {
			chances.push(i);
		}

		return {
			numToPick: 3,
			chances,
		};
	}

	if (draftType === "nba1994") {
		return {
			numToPick: 3,
			chances: [250, 199, 156, 119, 88, 63, 43, 28, 17, 11, 8, 7, 6, 5],
		};
	}

	if (draftType === "nba2019") {
		return {
			numToPick: 4,
			chances: [140, 140, 140, 125, 105, 90, 75, 60, 45, 30, 20, 15, 10, 5],
		};
	}

	if (draftType === "nhl2017") {
		return {
			numToPick: 3,
			chances: [185, 135, 115, 95, 85, 75, 65, 60, 50, 35, 30, 25, 20, 15, 10],
		};
	}

	if (draftType === "nhl2021") {
		return {
			numToPick: 2,
			chances: [
				185, 135, 115, 95, 85, 75, 65, 60, 50, 35, 30, 25, 20, 15, 5, 5,
			],
		};
	}

	if (draftType === "mlb2022") {
		return {
			numToPick: 6,
			chances: [
				1650, 1650, 1650, 1325, 1000, 750, 550, 390, 270, 180, 140, 110, 90, 76,
				62, 48, 36, 23,
			],
		};
	}

	if (draftType === "custom") {
		return {
			numToPick: g.get("draftLotteryCustomNumPicks"),
			chances: [...g.get("draftLotteryCustomChances")],
		};
	}

	if (draftType === "cola") {
		return {
			numToPick: 4,
			chances: [1], // Placeholder, will be filled with real values later
		};
	}

	throw new Error(`Unsupported draft type "${draftType}"`);
};

const draftHasLottery = (
	draftType: any,
): draftType is (typeof LOTTERY_DRAFT_TYPES)[number] => {
	return LOTTERY_DRAFT_TYPES.includes(draftType);
};

export const getNumToPick = (
	draftType: DraftType | "dummy" | undefined,
	numLotteryTeams: number,
) => {
	if (draftHasLottery(draftType)) {
		return getLotteryInfo(draftType, numLotteryTeams).numToPick;
	}

	return 0;
};

export class NotEnoughTeamsError extends Error {
	constructor(numFirstRoundTeams: number, draftType: DraftType) {
		super(
			`Number of teams with draft picks (${numFirstRoundTeams}) is less than the minimum required for draft type "${draftType}"`,
		);
		this.name = "NotEnoughTeamsError";
	}
}

/**
 * Sets draft order and save it to the draftPicks object store.
 *
 * If mock is true, then nothing is actually saved to the database and no notifications are sent
 */
const genOrder = async (
	mock: boolean = false,
	conditions?: Conditions,
	draftTypeOverride?: DraftType,
): Promise<ReturnVal> => {
	// Sometimes picks just fail to generate or get lost. For example, if numSeasonsFutureDraftPicks is 0.
	await genPicks();

	const draftPicks = await genOrderGetPicks(mock);
	const draftPicksIndexed: DraftPickWithoutKey[][] = [];
	for (const dp of draftPicks) {
		const tid = dp.originalTid;

		// Initialize to an array
		if (draftPicksIndexed[tid] === undefined) {
			draftPicksIndexed[tid] = [];
		}

		draftPicksIndexed[tid][dp.round] = dp;
	}

	const { teamsByRound, ties } = await getTeamsByRound(draftPicksIndexed);
	const firstRoundTeams = teamsByRound[0] ?? [];

	const draftType = draftTypeOverride ?? g.get("draftType");
		// Lottery override controls were removed, so this is always undefined.
	// Explicit type annotation keeps the downstream truthy branches type-safe
	// (otherwise tsc narrows to `never` and refuses .map() calls below).
	const riggedLottery = undefined as
		| GameAttributesLeague["riggedLottery"]
		| undefined;

	// Draft lottery
	const firstN: number[] = [];
	let numLotteryTeams = 0;
	let chances: number[] = [];
	if (draftHasLottery(draftType)) {
		const numPlayoffTeams = (await getNumPlayoffTeams(g.get("season")))
			.numPlayoffTeams;

		const info = getLotteryInfo(
			draftType,
			firstRoundTeams.length - numPlayoffTeams,
		);
		const numToPick = info.numToPick;

		if (firstRoundTeams.length < numToPick) {
			throw new NotEnoughTeamsError(firstRoundTeams.length, draftType);
		}

		if (draftType === "cola") {
			numLotteryTeams = await getNumLotteryTeams();
		} else {
			numLotteryTeams = helpers.bound(
				firstRoundTeams.length - numPlayoffTeams,
				numToPick,
				draftType === "coinFlip" ? numToPick : firstRoundTeams.length,
			);
		}

		if (draftType === "cola") {
			// If the playoffs aren't over yet, then we haven't yet added COLA_ALPHA to all the lottery teams
			const addAlpha = g.get("phase") <= PHASE.PLAYOFFS ? COLA_ALPHA : 0;

			const lotteryTeams = firstRoundTeams.slice(0, numLotteryTeams);
			chances = lotteryTeams.map((t) => {
				// Traded picks are not eligible for the lottery
				const currentTid = draftPicksIndexed[t.tid]?.[1]?.tid;
				if (currentTid !== t.tid) {
					return 0;
				}

				if (t.colaOptOut) {
					return 0;
				}

				return (t.cola ?? 0) + addAlpha;
			});
		} else {
			chances = info.chances;
		}

		if (numLotteryTeams < chances.length) {
			chances = chances.slice(0, numLotteryTeams);
		} else {
			while (numLotteryTeams > chances.length) {
				chances.push(chances.at(-1)!);
			}
		}

		if (draftType !== "cola") {
			divideChancesOverTiedTeams(chances, firstRoundTeams, true);
		}

		const chanceTotal = chances.reduce((a, b) => a + b, 0);
		const chancePct = chances.map((c) => (c / chanceTotal) * 100);

		// Idenfity chances indexes protected by riggedLottery, and set to 0 in chancesCumsum
		const riggedLotteryChances = riggedLottery
			? riggedLottery.map((dpid) => {
					if (typeof dpid === "number") {
						const originalTid = draftPicks.find((dp) => {
							return dp.dpid === dpid;
						})?.originalTid;
						if (originalTid !== undefined) {
							const index = firstRoundTeams.findIndex(
								({ tid }) => tid === originalTid,
							);
							if (index >= 0) {
								return index;
							}
						}
					}

					return null;
				})
			: undefined;

		const chancesCumsum = chances.slice();
		if (riggedLotteryChances?.includes(0)) {
			chancesCumsum[0] = 0;
		}
		for (let i = 1; i < chancesCumsum.length; i++) {
			if (riggedLotteryChances?.includes(i)) {
				chancesCumsum[i] = chancesCumsum[i - 1]!;
			} else {
				chancesCumsum[i]! += chancesCumsum[i - 1]!;
			}
		}

		const totalChances = chancesCumsum.at(-1)!;

		// Pick first 3 or 4 picks based on chancesCumsum
		let iterations = 0;
		while (firstN.length < numToPick) {
			if (riggedLotteryChances) {
				const index = riggedLotteryChances[firstN.length];
				if (typeof index === "number") {
					firstN.push(index);
					continue;
				}
			}

			const draw = random.randInt(0, totalChances - 1);
			const i = chancesCumsum.findIndex((chance) => chance > draw);

			// This happens if all the chances are 0, such which can happen in cola if everyone opts out. In that case, just pick teams from uniform random. In that case, don't make any lottery selection and just proceed in order. UI is still messed up in that case, showing NaNs, but whatever.
			if (i < 0) {
				break;
			}

			if (
				!firstN.includes(i) &&
				i < firstRoundTeams.length &&
				draftPicksIndexed[firstRoundTeams[i]!.tid]
			) {
				firstN.push(i);
			}

			iterations += 1;
			if (iterations > 100000) {
				break;
			}
		}

		if (!mock) {
			logLotteryChances(
				chancePct,
				firstRoundTeams,
				draftPicksIndexed,
				conditions,
			);
		}
	} else {
		for (const roundTeams of teamsByRound) {
			if (draftType === "random") {
				random.shuffle(roundTeams);
			} else if (draftType === "noLotteryReverse") {
				roundTeams.reverse();
			}
		}
	}

	const firstRoundOrderAfterLottery = [];

	// First round - lottery winners
	let pick = 1;
	for (let i = 0; i < firstN.length; i++) {
		const t = firstRoundTeams[firstN[i]!]!;
		const dp = draftPicksIndexed[t.tid]![1];

		if (dp !== undefined) {
			dp.pick = pick;
			firstRoundOrderAfterLottery.push(t);

			if (!mock) {
				logLotteryWinners(
					firstRoundTeams,
					dp.tid,
					firstRoundTeams[firstN[i]!]!.tid,
					pick,
					conditions,
				);
			}

			pick += 1;
		}
	}

	if (!mock && draftType === "cola") {
		const tids = firstRoundOrderAfterLottery.map((t) => t.tid);
		await updateLotteryChancesAfterLottery(tids);
	}

	// First round - everyone else
	for (const [i, t] of firstRoundTeams.entries()) {
		if (!firstN.includes(i)) {
			const dp = draftPicksIndexed[t.tid]?.[1];

			if (dp) {
				dp.pick = pick;
				firstRoundOrderAfterLottery.push(t);

				if (pick <= numLotteryTeams && !mock) {
					logLotteryWinners(firstRoundTeams, dp.tid, t.tid, pick, conditions);
				}

				pick += 1;
			}
		}
	}

	let draftLotteryResult: ReturnVal["draftLotteryResult"];
	if (draftHasLottery(draftType)) {
		// Save draft lottery results separately
		draftLotteryResult = {
			season: g.get("season"),
			draftType,
			rigged: riggedLottery,
			result: firstRoundTeams // Start with teams in lottery order
				.map(({ tid }) => {
					return draftPicks.find((dp) => {
						// Keep only lottery picks
						return (
							dp.originalTid === tid &&
							dp.round === 1 &&
							dp.pick > 0 &&
							dp.pick <= chances.length
						);
					});
				})
				.filter((dp) => dp !== undefined) // Keep only lottery picks
				.map((dp) => {
					if (dp === undefined) {
						throw new Error("Should never happen");
					}

					// For the original team
					const i = firstRoundTeams.findIndex(
						(t2) => t2.tid === dp.originalTid,
					);

					return {
						tid: dp.tid,
						originalTid: dp.originalTid,
						chances: chances[i]!,
						pick: dp.pick,
						dpid: dp.dpid,
					};
				}),
		};

		if (!mock) {
			await idb.cache.draftLotteryResults.put(draftLotteryResult);
			await league.setGameAttributes({
				riggedLottery: undefined,
			});
		}
	}

	for (let roundIndex = 1; roundIndex < teamsByRound.length; roundIndex++) {
		const roundTeams = teamsByRound[roundIndex]!;
		const round = roundIndex + 1;

		// Handle tiebreakers for the 2nd+ round (1st is already done by getTeamsByRound, but 2nd can't be done until now because it depends on lottery results)
		// Skip random drafts because this code assumes teams appear in the same order every round, which is not true there!
		if (draftType !== "random") {
			for (const { rounds, teams } of Object.values(ties)) {
				if (rounds.includes(round)) {
					// From getTeamsByRound, teams is guaranteed to be a continuous section of roundTeams, so we can just figure out the correct order for them and then replace them in roundTeam
					const start = roundTeams.findIndex((t) => teams.includes(t));
					const length = teams.length;

					const firstRoundOrder = firstRoundOrderAfterLottery.filter((t) =>
						teams.includes(t),
					);

					// Handle case where a team did not appear in the 1st round but does now, which probably never happens
					for (const t of teams) {
						if (!firstRoundOrder.includes(t)) {
							firstRoundOrder.push(t);
						}
					}

					// Basketball swaps tied-team order every other round.
					const newOrder = firstRoundOrder;
					if (roundIndex % 2 === 1) {
						newOrder.reverse();
					}
					roundTeams.splice(start, length, ...newOrder);
				}
			}
		}

		let pick = 1;
		for (const t of roundTeams) {
			const dp = draftPicksIndexed[t.tid]?.[roundIndex + 1];

			if (dp !== undefined) {
				dp.pick = pick;
				pick += 1;
			}
		}
	}

	if (!mock) {
		for (const dp of draftPicks) {
			await idb.cache.draftPicks.put(dp);
		}

		await league.setGameAttributes({
			numDraftPicksCurrent: draftPicks.length,
		});
	}

	return { draftLotteryResult, draftPicks };
};

export default genOrder;
